import { useState, useEffect } from "react"
import { useParams } from "react-router"
import BirddogLayout from "../components/BirddogLayout"
import { fetchSale } from "../services/salesApi"
import { Sale } from "../types"
import { LumpSumBidForm } from "../components/LumpSumBidForm"
import { PayAsCutBidForm } from "../components/PayAsCutBidForm"

export const BidCreate = () => {
  const { saleId } = useParams<{ saleId: string }>()

  const [sale, setSale] = useState<Sale | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch sale data from API
  useEffect(() => {
    const loadSale = async () => {
      if (!saleId) {
        setError("Sale ID is required")
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const saleData = await fetchSale(Number(saleId))
        setSale(saleData)
      } catch (err) {
        if (err instanceof Error) {
          if (err.message === "Sale not found") {
            setError("Sale not found")
          } else {
            setError(`Failed to load sale: ${err.message}`)
          }
        } else {
          setError("An unexpected error occurred")
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadSale()
  }, [saleId])

  // Handle loading state
  if (isLoading) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading sale details...</p>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Handle error state
  if (error) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {error === "Sale not found" ? "Sale Not Found" : "Error"}
            </h1>
            <p className="text-gray-600 mt-2">
              {error === "Sale not found"
                ? "The requested sale could not be found."
                : error}
            </p>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Handle case where sale is still null after loading
  if (!sale) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Sale Not Found</h1>
            <p className="text-gray-600 mt-2">
              The requested sale could not be found.
            </p>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  return (
    <BirddogLayout>
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Create Bid</h1>
        <p className="text-gray-600 mt-2">Submit a bid for: {sale.name}</p>

        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Bid Form
            </h2>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700">
                Sale Type
              </label>
              <p className="text-gray-900 capitalize">{sale.sale_type}</p>
            </div>

            {sale.sale_type === "lump sum" ? (
              <LumpSumBidForm sale={sale} />
            ) : (
              <PayAsCutBidForm sale={sale} />
            )}
          </div>
        </div>
      </div>
    </BirddogLayout>
  )
}
